<template>
  <div class="container-fluid p-4">
    <div class="row justify-content-center">
      <div class="col-12 col-lg-10 col-xl-8">
        <div class="pb-4 d-flex flex-column justify-content-between border-bottom mb-4">
          <small class="text-muted mb-1 ">
            <font-awesome-icon icon="cog" fixed-width class="mr-1" />{{$t("global.ansichtBearbeiten")}}: {{$t("altbestaende.titel")}}
          </small>
          <h1 class="h2">{{$t("altbestaende.bearbeiten")}}</h1>
        </div>

        <!-- File Upload Section -->
        <div class="mb-4">
          <b-form-file
            v-model="selectedFile"
            accept=".txt"
            @change="handleFileSelect"
            placeholder="Datei auswählen oder hierher ziehen und ablegen"
            drop-placeholder="Datei hier ablegen"
            ref="fileInput"
            :disabled="isProcessing">
          </b-form-file>
          <small class="text-muted mt-2 d-block">
            Unterstützte Formate: .txt (Tabstopp-getrennt)
          </small>
        </div>

        <!-- Processing Status -->
        <div v-if="isProcessing" class="text-center mb-4">
          <div class="spinner-border text-primary" role="status">
            <span class="sr-only">Verarbeitung läuft...</span>
          </div>
          <p class="mt-2">Datei wird verarbeitet...</p>
        </div>

        <!-- Preview Section -->
        <div v-if="previewData.length > 0 && !isProcessing" class="mb-4">
          <div class="alert alert-info">
            <strong>Vorschau der ersten 5 Zeilen:</strong> Bitte überprüfen Sie die Daten vor dem Import.
          </div>
          
          <div class="table-responsive">
            <table class="table table-sm table-bordered">
              <thead class="thead-light">
                <tr>
                  <th v-for="(header, index) in headers" :key="index">
                    {{ header }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, rowIndex) in previewData" :key="rowIndex">
                  <td v-for="(cell, cellIndex) in row" :key="cellIndex">
                    {{ cell }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="text-center mt-3">
            <b-button 
              variant="primary" 
              @click="importData"
              :disabled="isImporting">
              <span v-if="isImporting" class="spinner-border spinner-border-sm mr-2" role="status"></span>
              {{ isImporting ? 'Lade hoch...' : `Datei hochladen (${totalRows} Zeilen)` }}
            </b-button>
            <b-button 
              variant="secondary" 
              @click="resetImport"
              class="ml-2"
              :disabled="isImporting">
              Abbrechen
            </b-button>
          </div>
        </div>

        <!-- Success/Error Messages -->
        <div v-if="importMessage" class="mt-4">
          <div :class="['alert', importSuccess ? 'alert-success' : 'alert-danger']">
            {{ importMessage }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Papa from 'papaparse'
import { storage } from '../../../firebase'
import { ref, uploadBytes } from 'firebase/storage'

export default {
  name: 'BearbeiteAltbestaende',
  data() {
    return {
      selectedFile: null,
      previewData: [],
      headers: [],
      totalRows: 0,
      isProcessing: false,
      isImporting: false,
      importMessage: '',
      importSuccess: false,
      allData: []
    }
  },
  methods: {
    handleFileSelect(event) {
      const file = event.target.files[0] || this.selectedFile
      if (file) {
        this.parseFile(file)
      }
    },

    parseFile(file) {
      this.isProcessing = true
      this.resetMessages()

      const fileReader = new FileReader()
      fileReader.readAsText(file, 'UTF-8')
      
      fileReader.onload = (e) => {
        try {
          const csvData = Papa.parse(e.target.result, {
            delimiter: '\t', // Tabstopp-getrennt
            skipEmptyLines: true,
            transformHeader: (header, index) => `Spalte ${index + 1}`,
            transform: (value) => value.trim() // Whitespace entfernen
          })

          if (csvData.errors.length > 0) {
            console.warn('Parse-Warnungen:', csvData.errors)
          }

          this.allData = csvData.data
          
          if (this.allData.length > 0) {
            // Erste Zeile als Header verwenden
            this.headers = this.allData[0]
            
            // Datenzeilen (ohne Header)
            const dataRows = this.allData.slice(1)
            this.totalRows = dataRows.length
            
            // Vorschau der ersten 5 Datenzeilen (ohne Header)
            this.previewData = dataRows.slice(0, Math.min(5, dataRows.length))
          } else {
            this.showMessage('Die Datei enthält keine Daten.', false)
          }
        } catch (error) {
          console.error('Fehler beim Parsen der Datei:', error)
          this.showMessage('Fehler beim Lesen der Datei. Bitte stellen Sie sicher, dass es sich um eine gültige tabstopp-getrennte txt-Datei handelt.', false)
        } finally {
          this.isProcessing = false
        }
      }

      fileReader.onerror = () => {
        this.showMessage('Fehler beim Lesen der Datei.', false)
        this.isProcessing = false
      }
    },

    async importData() {
      if (!this.selectedFile) {
        this.showMessage('Keine Datei zum Hochladen ausgewählt.', false)
        return
      }

      this.isImporting = true
      this.resetMessages()

      try {
        // Erstelle einen eindeutigen Dateinamen mit Zeitstempel
        const fileName = `altbestaende.txt`
        
        // Erstelle eine Referenz im Storage Bucket unter dem Pfad 'oldStocks/'
        const storageRef = ref(storage, `oldStocks/${fileName}`)
        
        // Lade die Datei in Firebase Storage hoch
        const uploadResult = await uploadBytes(storageRef, this.selectedFile)
        console.log('Datei erfolgreich hochgeladen:', uploadResult)

        this.showMessage(`Datei "${this.selectedFile.name}" wurde erfolgreich in Firebase Storage hochgeladen (${this.totalRows} Zeilen).`, true)
        
        // Lade die Altbestände im Store neu
        this.$store.dispatch('fetchAltbestaende')
        
        // Nach erfolgreichem Upload zurücksetzen
        setTimeout(() => {
          this.resetImport()
        }, 3000)

      } catch (error) {
        console.error('Fehler beim Upload:', error)
        this.showMessage('Fehler beim Hochladen der Datei. Bitte versuchen Sie es erneut.', false)
      } finally {
        this.isImporting = false
      }
    },

    resetImport() {
      this.selectedFile = null
      this.previewData = []
      this.headers = []
      this.totalRows = 0
      this.allData = []
      this.resetMessages()
      this.$refs.fileInput.reset()
    },

    resetMessages() {
      this.importMessage = ''
      this.importSuccess = false
    },

    showMessage(message, success) {
      this.importMessage = message
      this.importSuccess = success
    }
  }
}
</script>
